import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
} 

export type TrpcEnvelope<T> =
  | T
  | { json?: T }
  | { result?: { data?: { json?: T } } };

export function unwrapTrpcData<T>(payload: TrpcEnvelope<T> | null | undefined): T | undefined {
  if (!payload) return undefined;

  if (Array.isArray(payload)) {
    return payload as T;
  }

  if (typeof payload === 'object') {
    if (Array.isArray((payload as any).json)) {
      return (payload as any).json;
    }

    const nested = (payload as any).result?.data?.json;
    if (Array.isArray(nested)) {
      return nested as T;
    }
  }

  return payload as T;
}

export type VideoEmbedProvider = 'youtube' | 'vimeo' | 'loom';

export interface VideoEmbedInfo {
  provider: VideoEmbedProvider;
  embedUrl: string;
}

const YOUTUBE_REGEX = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i;
const VIMEO_REGEX = /vimeo\.com\/(?:video\/)?(\d+)/i;
const LOOM_REGEX = /loom\.com\/(?:share|embed)\/([\w-]+)/i;

function extractYoutubeId(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu')) {
      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.split('/shorts/')[1]?.split('/')[0] ?? null;
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.split('/embed/')[1]?.split(/[/?#]/)[0] ?? null;
      }
      const vParam = parsed.searchParams.get('v');
      if (vParam) {
        return vParam;
      }
    }
  } catch {
    // Ignore URL parsing failures and fall back to regex
  }
  const match = url.match(YOUTUBE_REGEX);
  return match?.[1] ?? null;
}

function extractVimeoId(url: string): string | null {
  const match = url.match(VIMEO_REGEX);
  return match?.[1] ?? null;
}

function extractLoomId(url: string): string | null {
  const match = url.match(LOOM_REGEX);
  return match?.[1] ?? null;
}

export function getVideoEmbedInfo(url?: string | null): VideoEmbedInfo | null {
  const normalized = url?.trim();
  if (!normalized) {
    return null;
  }

  const youtubeId = extractYoutubeId(normalized);
  if (youtubeId) {
    return {
      provider: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
    };
  }

  const vimeoId = extractVimeoId(normalized);
  if (vimeoId) {
    return {
      provider: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
    };
  }

  const loomId = extractLoomId(normalized);
  if (loomId) {
    return {
      provider: 'loom',
      embedUrl: `https://www.loom.com/embed/${loomId}`,
    };
  }

  return null;
}

export function isEmbeddableVideoUrl(url?: string | null): boolean {
  return Boolean(getVideoEmbedInfo(url));
}